import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { SupplierDto } from '@/types/materials';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    gstin: z.string().nullable(),
    address: z.string().nullable(),
    contactPerson: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    status: z.enum(['Active', 'Inactive']),
    // Only surfaced while Job Work is enabled — a supplier saved before/while the module is off
    // simply keeps this false, so the field is inert rather than hidden-but-required.
    isJobWorkVendor: z.boolean(),
  });
}

export type SupplierFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: SupplierFormValues = {
  code: '', name: '', gstin: null, address: null, contactPerson: null, phone: null, email: null, status: 'Active',
  isJobWorkVendor: false,
};

interface SupplierFormDialogProps {
  open: boolean;
  supplier: SupplierDto | null;
  onClose: () => void;
  onSubmit: (values: SupplierFormValues) => void;
  loading?: boolean;
}

export default function SupplierFormDialog({ open, supplier, onClose, onSubmit, loading }: SupplierFormDialogProps) {
  const { t } = useTranslation('supplierMaster');
  const schema = buildSchema(t);
  const { data: config } = useProcessConfiguration();

  const { control, handleSubmit, reset } = useForm<SupplierFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      reset(
        supplier
          ? {
              code: supplier.code, name: supplier.name, gstin: supplier.gstin, address: supplier.address,
              contactPerson: supplier.contactPerson, phone: supplier.phone, email: supplier.email,
              status: supplier.status as 'Active' | 'Inactive',
              isJobWorkVendor: supplier.isJobWorkVendor ?? false,
            }
          : DEFAULTS,
      );
    }
  }, [open, supplier, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{supplier ? supplier.name : t('addSupplier')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.code')} fullWidth disabled={Boolean(supplier)} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="gstin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.gstin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="contactPerson" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.contactPerson')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="phone" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.phone')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="email" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.email')} fullWidth type="email" />} />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.address')} fullWidth multiline rows={2} />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.status')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
          {config?.jobWorkEnabled && (
            <GridItem xs={2}>
              <Controller
                name="isJobWorkVendor"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('form.isJobWorkVendor')}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('form.isJobWorkVendorHint')}
                    </Typography>
                  </>
                )}
              />
            </GridItem>
          )}
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveSupplier')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
